/**
 * Calendar page: {@link CalendarView} and sync modal.
 */
import * as React from 'react';
import { useSearchParams } from 'react-router-dom';
import { RefreshCw } from 'lucide-react';
import { CalendarSyncModal } from '@/components/calendar/CalendarSyncModal';
import { CalendarView } from '@/components/calendar/CalendarView';
import { CALENDAR_SYNC_ENABLED } from '@/config/features';
import { ManagementPage } from '@/components/common/ManagementPage';
import { Button } from '@/components/forms/Button';
import { toast } from '@/components/ui/toast';
import { getErrorMessage } from '@/utils/errors-utils';
import type { CalendarSyncFilterSeed } from '@/utils/calendar-sync-utils';

/** Staff calendar with sync to Google Agenda / Outlook. */
export const CalendarPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [syncOpen, setSyncOpen] = React.useState(false);
  const [filterSeed, setFilterSeed] = React.useState<CalendarSyncFilterSeed>({});

  React.useEffect(() => {
    const sync = searchParams.get('sync');
    if (!CALENDAR_SYNC_ENABLED || !sync) {
      return;
    }
    if (sync === 'connected') {
      setSyncOpen(true);
      toast.success(getErrorMessage('calendarOauthConnected'));
    } else if (sync === 'error') {
      setSyncOpen(true);
      toast.error(getErrorMessage('calendarOauthDenied'));
    }
    searchParams.delete('sync');
    searchParams.delete('provider');
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, setSearchParams]);

  function handleOpenSync(): void {
    setSyncOpen(true);
  }

  function handleCloseSync(): void {
    setSyncOpen(false);
  }

  return (
    <ManagementPage
      title="Calendrier"
      subtitle="Tout votre planning au même endroit"
      actions={
        CALENDAR_SYNC_ENABLED ? (
          <Button preset="export" icon={RefreshCw} onClick={handleOpenSync}>
            Synchroniser
          </Button>
        ) : undefined
      }
    >
      <CalendarView onSyncSeedChange={setFilterSeed} />
      {CALENDAR_SYNC_ENABLED ? (
        <CalendarSyncModal open={syncOpen} onClose={handleCloseSync} filterSeed={filterSeed} />
      ) : null}
    </ManagementPage>
  );
};
